import type { TFunction } from "i18next";
import {
  REFERRAL_SPECIALTIES,
  type ReferralSpecialtyKey,
} from "@/lib/referral-templates/specialties";

// Template library categories are stored in English ("Cardiology", "ENT",
// "GP"). In another interface language they read as the specialty's name
// there: the referral specialties' names where they match, else a few
// library-only names. Categories not listed here are shown as stored.

type LibraryOnlyKey =
  | "emergency"
  | "familyMedicine"
  | "generalPractice"
  | "obgyn"
  | "pulmonology"
  | "nursing"
  | "psychology"
  | "occupationalTherapy"
  | "chiropractic"
  | "pharmacy"
  | "radiology"
  | "anesthesiology"
  | "general"
  | "specialist"
  | "dictation"
  | "imaging"
  | "international"
  | "uncategorized";

const fold = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Folded category name -> referral specialty key.
const REFERRAL_ALIASES: Record<string, ReferralSpecialtyKey> = {
  ...Object.fromEntries(
    REFERRAL_SPECIALTIES.flatMap(([key, name]) => [
      [fold(name), key],
      [fold(key), key],
    ]),
  ),
  allergy: "allergy",
  "allergy and immunology": "allergy",
  immunology: "allergy",
  ent: "otolaryngology",
  otolaryngology: "otolaryngology",
  geriatrician: "geriatrics",
  geriatrics: "geriatrics",
  "geriatric medicine": "geriatrics",
  orthopedics: "orthopedics",
  orthopaedics: "orthopedics",
  "orthopedic surgery": "orthopedics",
  "infectious disease": "infectious-diseases",
  "pain management": "pain",
  "palliative medicine": "palliative",
  "physical medicine": "physiatry",
  physiatry: "physiatry",
  "mental health": "mental-health",
  nutrition: "dietitian",
  pediatric: "pediatrics",
  paediatrics: "pediatrics",
  haematology: "hematology",
  gynaecology: "gynecology",
};

const LIBRARY_ALIASES: Record<string, LibraryOnlyKey> = {
  "emergency medicine": "emergency",
  emergency: "emergency",
  "family medicine": "familyMedicine",
  "family practice": "familyMedicine",
  gp: "generalPractice",
  "general practice": "generalPractice",
  "general practitioner": "generalPractice",
  obgyn: "obgyn",
  "ob gyn": "obgyn",
  "obstetrics and gynecology": "obgyn",
  "obstetrics gynecology": "obgyn",
  pulmonology: "pulmonology",
  nursing: "nursing",
  psychology: "psychology",
  "occupational therapy": "occupationalTherapy",
  chiropractic: "chiropractic",
  chiropractor: "chiropractic",
  pharmacy: "pharmacy",
  radiology: "radiology",
  anesthesiology: "anesthesiology",
  anaesthesiology: "anesthesiology",
  general: "general",
  specialist: "specialist",
  dictation: "dictation",
  imaging: "imaging",
  international: "international",
  uncategorized: "uncategorized",
};

export const libraryCategoryLabel = (
  category: string,
  t: TFunction,
  language: string,
): string => {
  // English is how they are stored (and how the library team named them).
  if (language.toLowerCase().startsWith("en")) return category;
  const folded = fold(category);
  const library = LIBRARY_ALIASES[folded];
  if (library) return t(`templates.library.categories.${library}`);
  const referral = REFERRAL_ALIASES[folded];
  if (referral) return t(`referral.specialties.${referral}`);
  return category;
};
